/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import Hidden from '@material-ui/core/Hidden';
import Joi from '@hapi/joi';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { ErrorMessage } from '@hookform/error-message';
import config from '@/configs/config';
import formErrorMessage from '@/configs/formErrorMessage';
import { useAuthState } from '@/plugins/Auth';
import { useProgressContext } from '@/contexts/useProgressContext';

import TextField from '@/components/TextField';
import Alert from '@/components/Alert';

import preOrderAPI from '@/api/preOrder';

const STEP = {
  ENTER_PHONENUBMER: 'ENTER_PHONENUBMER',
  ENTER_VERIFICATION_CODE: 'ENTER_VERIFICATION_CODE',
};

const schema = Joi.object({
  phoneNumber: Joi.string()
    .pattern(/^09\d{2}-?\d{3}-?\d{3}$/, 'phoneNumber')
    .required()
    .messages({
      'string.pattern.name': '您輸入的手機號碼格式錯誤',
    }),
  verificationCode: Joi.string()
    .pattern(/^\d{6}$/, 'verificationCode')
    .required()
    .messages({
      'string.pattern.name': '請輸入六位數字驗證碼',
    }),
});

const OAuthConnectPhoneNumber = () => {
  const history = useHistory();
  const {
    state: routerState,
  } = useLocation();
  const { provider } = useParams();
  const { token } = useAuthState();
  const {
    start: startProgress,
    end: endProgress,
  } = useProgressContext();
  const [step, setStep] = React.useState(STEP.ENTER_PHONENUBMER);
  const [alert, setAlert] = React.useState(false);

  const {
    errors,
    control,
    watch,
    formState,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      phoneNumber: '09',
      verificationCode: '',
    },
    resolver: joiResolver(schema, {
      abortEarly: false,
      messages: formErrorMessage,
    }),
  });

  const phoneNumber = watch('phoneNumber');
  const verificationCode = watch('verificationCode');

  const handleSendVerificationCode = React.useCallback(() => {
    startProgress();
    preOrderAPI.sendVerificationCode({
      body: {
        phoneNumber,
        token,
      },
    }).then(({ status, message }) => {
      /*
        TODO: refactor token expired part once we redesign backend api
        for error handler and response interface.
      */
      if (status === 'error' && message.includes('您已經註冊過了')) {
        history.push('/');
        return;
      }

      if (status !== 'OK') {
        setAlert(message);
        return;
      }
      setStep(STEP.ENTER_VERIFICATION_CODE);
    }).finally(() => {
      endProgress();
    });
  }, [
    phoneNumber,
    token,
    history,
    startProgress,
    endProgress,
  ]);

  const handleVerifyVerificationCode = React.useCallback(() => {
    startProgress();
    preOrderAPI.verifyVerificationCode({
      body: {
        verificationCode,
        token,
      },
    }).then(({ status, message }) => {
      if (status !== 'OK') {
        setAlert(message);
        return;
      }

      history.push({
        pathname: `/auth/${provider}/login`,
        state: routerState,
      });
    }).finally(() => {
      endProgress();
    });
  }, [
    verificationCode,
    token,
    history,
    provider,
    routerState,
    startProgress,
    endProgress,
  ]);

  const actionButtons = (
    <Button
      type="submit"
      variant="contained"
      color="primary"
      fullWidth
      onClick={handleVerifyVerificationCode}
      disabled={!formState.isValid}
    >
      <Box
        py={2}
        fontSize="body1.fontSize"
        fontWeight="fontWeightBold"
      >
        下一步
      </Box>
    </Button>
  );
  return (
    <Box bgcolor="grey.300" minHeight="calc(100vh - 100px)" position="relative">
      <Box position="relative">
        <img src="/images/PreOrder/background.png" style={{ width: '100%', maxHeight: 300 }} alt="" />
        <Box
          position="absolute"
          width={1}
          bottom={60}
        >
          <Grid container direction="column" justify="center" spacing={1}>
            <Grid item>
              <Box
                color="primary.main"
                fontSize="h5.fontSize"
                fontWeight="fontWeightBold"
                textAlign="center"
              >
                { step === STEP.ENTER_PHONENUBMER ? '請輸入您的手機號碼' : '輸入驗證碼' }
              </Box>
            </Grid>
            <Grid item>
              <Box
                color="common.white"
                fontSize="body1.fontSize"
                fontWeight="fontWeightBold"
                textAlign="center"
              >
                完成
                {config.BrandNameTW}
                會員註冊
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box
        px={3}
        py={2}
        mt={4}
        mb={2}
        fontSize="subtitle1.fontSize"
        fontWeight="fontWeightBold"
      >
        <Box
          mb={4}
          color="grey.600"
        >
          感謝您的訂購，請先輸入您的手機號碼來收取到貨通知，以順利完成後續取貨流程。
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box py={1} color="grey.600">您輸入的手機號碼</Box>
            <Box
              bgcolor="common.white"
            >
              <Grid container>
                <Grid item xs>
                  <Controller
                    control={control}
                    name="phoneNumber"
                    as={<TextField />}
                  />
                </Grid>
                <Grid item>
                  <Button disabled={step === STEP.ENTER_VERIFICATION_CODE}>
                    <Box
                      px={1}
                      py={0.3}
                      color="common.white"
                      bgcolor={step !== STEP.ENTER_VERIFICATION_CODE ? 'support.main' : 'grey.500'}
                      borderRadius={2}
                      onClick={handleSendVerificationCode}
                    >
                      確認
                    </Box>
                  </Button>
                </Grid>
              </Grid>
            </Box>
            <Box mt={3} color="error.dark" fontSize="body2.fontSize">
              <ErrorMessage errors={errors} name="phoneNumber" />
            </Box>
            {
              step === STEP.ENTER_VERIFICATION_CODE && (
                <Box mt={3} color="error.dark" fontSize="body2.fontSize">
                  {`已發送六位數字驗證碼至${phoneNumber}的手機號碼裡`}
                </Box>
              )
            }
          </Grid>
          {
            step === STEP.ENTER_VERIFICATION_CODE && (
              <>
                <Grid item xs={12}>
                  <Box py={1} color="grey.600">請輸入驗證碼</Box>
                  <Controller
                    control={control}
                    name="verificationCode"
                    as={<TextField />}
                  />
                  <Box mt={3} color="error.dark" fontSize="body2.fontSize">
                    <ErrorMessage errors={errors} name="verificationCode" />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    color="grey.600"
                    textAlign="right"
                  >
                    <Link
                      component="button"
                      color="textSecondary"
                      underline="always"
                      onClick={handleSendVerificationCode}
                    >
                      我沒收到驗證碼
                    </Link>
                  </Box>
                </Grid>
              </>
            )
          }
        </Grid>
      </Box>
      <Hidden only="sm">
        <Box
          width={1}
          position="fixed"
          bottom={0}
          bgcolor="common.white"
          style={{
            paddingBottom: 'env(safe-area-inset-bottom, 0)',
          }}
        >
          {actionButtons}
        </Box>
      </Hidden>
      <Hidden only="xs">
        <Box
          width={1}
          position="absolute"
          bottom={0}
          bgcolor="common.white"
          style={{
            paddingBottom: 'env(safe-area-inset-bottom, 0)',
          }}
        >
          {actionButtons}
        </Box>
      </Hidden>
      <Alert
        isOpen={!!alert}
        title="會員註冊失敗"
        content={`${alert}`}
        buttons={(
          <Button fullWidth onClick={() => setAlert(null)}>
            <Box fontWeight="fontWeightBold" color="support.main">
              確認
            </Box>
          </Button>
        )}
      />
    </Box>
  );
};

export default OAuthConnectPhoneNumber;
